import { useNavigate } from 'react-router';

import Crown from '@/assets/icons/Crown';
import Button from '@/components/Buttons/Button';
import { EXPORT_NAME_MAX_LENGTH } from '@/constants/name';
import { Permission } from '@/entities/permissions';
import { biPlatformsGroup } from '@/features/DataHub/modules/Export/constants/biPlatforms';
import { optionById } from '@/features/DataHub/modules/Export/constants/exportOptions';
import {
  useExportMeta,
  useExportNameMeta,
  useExportPreview,
  useExportRequest,
  useExportSettings
} from '@/features/DataHub/modules/Export/context/exportContexts';
import { validateExportNameWithMaxLength } from '@/features/DataHub/modules/Export/services/ExportNameService';
import { getDateRangeValidation } from '@/features/DataHub/modules/Export/utils';
import type { ButtonConfig } from '@/features/DataHub/types';
import { useAppDispatch } from '@/hooks/redux-hooks';
import usePermission from '@/shared/hooks/usePermission';
import { closeDrawer } from '@/slices/drawerSlice';

const DataFooterButtons = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const { exportSettings } = useExportSettings();
  const { exportApiRequestString, exportCurlRequestString } = useExportMeta();
  const { exportName } = useExportNameMeta();
  const { previewOptionId, previewLoading, previewDownloadFn, previewContentToCopy } =
    useExportPreview();
  const {
    handleExportData,
    exportDataWithParams,
    handleCopyToClipBoard,
    capturePosthogEvent,
    loading,
    setExportStatus
  } = useExportRequest();

  const exportOption = previewOptionId ? (optionById.get(previewOptionId) ?? null) : null;

  const isIntegrationExport =
    exportOption?.preview?.type === 'integration' || exportOption?.preview?.type === 'sql';

  /** Python, Power Query, CSV, curl (json) etc. – content is in the preview; download = local file only, no API call */
  const isClientSideDownloadOnly =
    exportOption?.preview?.type === 'code' || exportOption?.preview?.type === 'simple';

  const shouldShowUpgradeButtons = !hasPermission(Permission.ExportBasic);

  const buttons: ButtonConfig[] = shouldShowUpgradeButtons
    ? [{ label: 'Upgrade plan', action: 'upgrade' }]
    : (exportOption?.buttons ?? []);
  const dateRangeValidation = getDateRangeValidation({
    dateRangeType: exportSettings.dateRangeType,
    from: exportSettings.from,
    to: exportSettings.to,
    periodNumber: exportSettings.periodNumber
  });
  const hasDateSettingsError =
    dateRangeValidation.periodNumberError ||
    dateRangeValidation.fromError ||
    dateRangeValidation.toError;

  const handleCopy = () => {
    const textToCopy = previewContentToCopy ?? exportCurlRequestString;
    handleCopyToClipBoard(textToCopy);
  };

  const handleDownloadOrExport = () => {
    capturePosthogEvent();

    // Integration exports (Power BI, Snowflake, etc.) → third-party API call
    if (isIntegrationExport) {
      setExportStatus('loading');
      exportDataWithParams({
        type: 'third-party',
        exportSubType: exportOption.id
      })
        .then(() => {
          setExportStatus('queued');
        })
        .catch(() => {
          setExportStatus('idle');
        });
      return;
    }

    // Client-side download exports (Python, CSV, curl, Power Query)
    if (isClientSideDownloadOnly && previewDownloadFn) {
      const skipPastExportSave =
        biPlatformsGroup.options.some(o => o.id === exportOption?.id) ||
        exportOption?.id === 'power_query';
      if (skipPastExportSave) {
        previewDownloadFn();
        return;
      }

      setExportStatus('loading');
      handleExportData()
        .then(() => {
          previewDownloadFn();
          setExportStatus('idle');
        })
        .catch(() => {
          setExportStatus('idle');
        });
      return;
    }

    // Server-side exports (Excel handles its own loading state)
    const isExcel = exportOption?.id === 'excel';
    if (!isExcel) {
      setExportStatus('loading');
    }

    handleExportData()
      .then(() => {
        previewDownloadFn?.();
        if (!isExcel) {
          setExportStatus('idle');
        }
      })
      .catch(() => {
        if (!isExcel) {
          setExportStatus('idle');
        }
      });
  };

  const handleLaunch = () => {
    capturePosthogEvent();
    setExportStatus('loading');
    handleExportData()
      .then(() => {
        if (exportApiRequestString) {
          window.open(exportApiRequestString, '_blank', 'noopener,noreferrer');
        }
      })
      .finally(() => {
        setExportStatus('idle');
      });
  };

  const handleUpgrade = () => {
    navigate('/manage-subscription', { replace: true });
    dispatch(closeDrawer());
  };

  const handleButtonClick = (button: ButtonConfig) => {
    if (button.action === 'copy') {
      handleCopy();
    } else if (button.action === 'download' || button.action === 'export') {
      handleDownloadOrExport();
    } else if (button.action === 'launch') {
      handleLaunch();
    } else if (button.action === 'upgrade') {
      handleUpgrade();
    } else if (button.onClick) {
      capturePosthogEvent();
      button.onClick();
    }
  };

  return (
    <>
      {buttons.length === 1 && (
        <Button
          sx={{
            color: 'primary.main',
            borderColor: 'primary.main'
          }}
          variant='outlined'
          onClick={() => {
            dispatch(closeDrawer());
          }}
        >
          Cancel
        </Button>
      )}
      {buttons.map(button => {
        const exportNameError =
          exportName === null
            ? 'Export name cannot be empty'
            : validateExportNameWithMaxLength(exportName, EXPORT_NAME_MAX_LENGTH);
        const isNameInvalid = Boolean(exportNameError);
        const isDisabled =
          button.disabled ||
          (button.action !== 'upgrade' &&
            (loading || previewLoading || isNameInvalid || hasDateSettingsError));

        return (
          <Button
            key={button.label}
            disabled={isDisabled}
            startIcon={button.action === 'upgrade' ? <Crown /> : undefined}
            variant={button.variant ?? 'contained'}
            onClick={() => {
              handleButtonClick(button);
            }}
          >
            {button.label}
          </Button>
        );
      })}
    </>
  );
};

export default DataFooterButtons;
